/**
 * data/quests/league — the Elite Four + Champion of the Summit (Round 3, elite-four.md).
 *
 * Five warm, distinct characters fought back-to-back (with a full heal between every
 * bout, exit/resume anytime) once all 8 badges are earned. Types deliberately avoid the
 * 8 gym types — the classic League palette: Ice, Ground, Ghost/Dark, Dragon, then a
 * balanced Champion ace. Every species EXISTS in the ingested Gen 1–2-focus dataset;
 * parties ramp L55→65, a gentle step above Badge 8's Cinder Vale (L52–55). Warm by
 * construction: they cheer you win OR lose, never gloat, never make you feel behind.
 *
 * Mirrors the GymLeader shape (leaders.ts): pure data. The renderer (LeagueScene) builds
 * real parties via society/makeInstance + PP hydrate; the pure engine resolves the fight.
 */

/** One authored League member + their faithful party and warm lines. */
export interface LeagueMember {
  /** 'elite-1'..'elite-4' | 'champion' — the gymId used by the defeat-gym-leader path. */
  readonly eliteId: string;
  /** 0..4 bout order (Elite 1 → 2 → 3 → 4 → Champion). */
  readonly order: number;
  readonly name: string;
  readonly title: string;
  readonly monType: string; // themed type
  readonly party: readonly { readonly speciesId: string; readonly level: number }[];
  readonly intro: readonly string[];
  readonly onWin: readonly string[]; // warm, never gloating (spoken when the PLAYER wins)
  readonly onLose: readonly string[]; // warm, no-stakes (spoken when the player loses)
}

export const LEAGUE_MEMBERS: readonly LeagueMember[] = [
  // ── Elite 1 · Ice ──
  {
    eliteId: 'elite-1',
    order: 0,
    name: 'Odalys',
    title: 'the Frostkind',
    monType: 'ice',
    party: [
      { speciesId: 'dewgong', level: 55 },
      { speciesId: 'cloyster', level: 56 },
      { speciesId: 'jynx', level: 56 },
      { speciesId: 'lapras', level: 57 },
    ],
    intro: [
      "Mind the ice — it's friendlier than it looks, and so am I.",
      "Cocoa's on the brazier for when we're done, win or lose. Come skate a while with me.",
    ],
    onWin: [
      'Cool head, warm heart — that’s how you cross ice. Beautifully done.',
      'Now — cocoa? You’ve earned the good mug, the big one.',
    ],
    onLose: [
      'Brr, took a tumble, did we? Happens to everyone on the ice.',
      'Sit by the brazier and warm up. Try me again whenever the mood takes you — no rush at all.',
    ],
  },
  // ── Elite 2 · Ground ──
  {
    eliteId: 'elite-2',
    order: 1,
    name: 'Terran Mudd',
    title: 'the Deep-Delver',
    monType: 'ground',
    party: [
      { speciesId: 'dugtrio', level: 57 },
      { speciesId: 'marowak', level: 58 },
      { speciesId: 'golem', level: 58 },
      { speciesId: 'rhydon', level: 59 },
    ],
    intro: [
      'Feet on solid ground, that’s all any of us really need!',
      'Give me your best, friend — the earth loves a good honest scrap.',
    ],
    onWin: [
      'HA! Shook the whole quarry, that did! Grand match, grand.',
      'You’ve earned the deep’s respect — and that’s bedrock, that is. Doesn’t shift.',
    ],
    onLose: [
      'Ground got the better of you today — no shame in solid rock!',
      'Dust yourself off, come back, and we’ll go again. The quarry keeps no clock.',
    ],
  },
  // ── Elite 3 · Ghost/Dark ──
  {
    eliteId: 'elite-3',
    order: 2,
    name: 'Vesper Wisp',
    title: 'the Kind Shade',
    monType: 'ghost',
    party: [
      { speciesId: 'murkrow', level: 59 },
      { speciesId: 'sneasel', level: 60 },
      { speciesId: 'houndoom', level: 60 },
      { speciesId: 'gengar', level: 61 },
    ],
    intro: [
      'Don’t fear the dark, love — it’s only the room where the good stories live.',
      'Shall we tell one together, you and I? The cozy sort, with a bright ending.',
    ],
    onWin: [
      'Ooh, a *bright* ending — my favorite kind. You told that beautifully.',
      'The shades adore you, you know. They’ll keep your story on the warm shelf.',
    ],
    onLose: [
      'The dark plays tricks, is all. No frights here, and certainly no losses.',
      'Just a story we’ll finish another night. Come back when you like — I’ll keep the lamp low and kind.',
    ],
  },
  // ── Elite 4 · Dragon ──
  {
    eliteId: 'elite-4',
    order: 3,
    name: 'Draca Emberwing',
    title: 'the Skywarden',
    monType: 'dragon',
    party: [
      { speciesId: 'gyarados', level: 61 },
      { speciesId: 'kingdra', level: 62 },
      { speciesId: 'charizard', level: 62 },
      { speciesId: 'dragonite', level: 63 },
    ],
    intro: [
      'Whole sky to ourselves and dragons for company — doesn’t get better than this!',
      'Up we go, champ. Show me those wings.',
    ],
    onWin: [
      'SKYBORNE, the both of you! What a flight — I felt it in my old bones!',
      'The Champion’s just past the doors. Go on, they’re expecting someone wonderful.',
    ],
    onLose: [
      'Caught a downdraft, that’s all! Every flyer does, and the best ones twice.',
      'The sky’s not going anywhere, and neither am I. Climb again when you’re ready.',
    ],
  },
  // ── Champion · balanced ace ──
  {
    eliteId: 'champion',
    order: 4,
    name: 'Solenne',
    title: 'the First Friend',
    monType: 'normal',
    party: [
      { speciesId: 'arcanine', level: 62 },
      { speciesId: 'alakazam', level: 63 },
      { speciesId: 'snorlax', level: 63 },
      { speciesId: 'starmie', level: 64 },
      { speciesId: 'dragonite', level: 64 },
      // Signature: dynamic "player's own favourite" is deferred (M-later); the authored
      // floor is Lapras — Solenne's own first friend, and a warm, fair closer.
      { speciesId: 'lapras', level: 65 },
    ],
    intro: [
      'I walked all eight badges once too, you know — and the best thing I found wasn’t a title.',
      'It was everyone I met on the way. Every one of them. Even the ones I lost to — especially those.',
      'Let’s have the warmest battle this hall has ever seen. Whenever you’re ready.',
    ],
    onWin: [
      'Champion. It suits you — but honestly? You were that the whole circuit, the way your team looks at you.',
      'Come — the whole town’s waiting to cheer, and they will not be talked out of it.',
      'And… if you ever feel a hush on the shrine step, go sit a while. Someone’s been hoping to meet a trainer exactly this kind.',
    ],
    onLose: [
      'Oh, what a match — I’m breathless! You gave me everything you had, I felt it.',
      'Rest up, and the throne’ll be right here when you fancy another go.',
      'No rush, ever. That was never what any of this was about.',
    ],
  },
];

/** O(1) lookup by eliteId ('elite-1'..'champion'). */
const MEMBER_BY_ID = new Map(LEAGUE_MEMBERS.map((m) => [m.eliteId, m]));

export function leagueMemberById(id: string): LeagueMember | undefined {
  return MEMBER_BY_ID.get(id);
}

/** The member at a given bout order (0..4), or undefined past the Champion. */
export function leagueMemberByOrder(order: number): LeagueMember | undefined {
  return LEAGUE_MEMBERS.find((m) => m.order === order);
}
